import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import { buildRoute } from '@/config/routes';
import { extractValidationMessage } from '@/util/validationError';
import {
  useCreatePublicLinkMutation,
  useRevokePublicLinkMutation
} from '../services/assetApi';

/** The URL a share token resolves to, on this origin. */
export const publicGalleryUrl = (token: string, origin: string = window.location.origin): string =>
  `${origin}${buildRoute.publicGallery(token)}`;

const COPIED_MS = 2000;

const GENERIC_400 =
  'The PlanOtour link can’t be changed right now. Refresh the page and try again.';
const GONE_404 = 'This photoset no longer exists. Refresh the page.';

export const PUBLIC_LINK_TOASTS = {
  on: 'PlanOtour link is on',
  off: 'PlanOtour link turned off',
  copyFailed: 'Couldn’t copy. Select the link and copy it manually.'
} as const;

/**
 * The alert text for a failed mint/revoke. asset-service refuses with
 * commons-core's validation envelope (400; the real reason, e.g. "The order is
 * not delivered yet…", rides in `code` after a fixed prefix while `message` is
 * boilerplate), so a 400's reason is read from there, or generic copy when it
 * has none. A 404 (unknown photoset) carries the same prefix but internal
 * wording ("Photoset not found: <uuid>"), so it gets fixed copy instead.
 * Anything else (403, network) falls through to the alert's own extraction.
 */
export const publicLinkErrorMessage = (error: unknown): string | undefined => {
  const status = (error as { status?: unknown } | null)?.status;
  if (status === 400) return extractValidationMessage(error) ?? GENERIC_400;
  if (status === 404) return GONE_404;
  return undefined;
};

interface UsePublicLinkArgs {
  photosetId: string;
  /** The photoset's share token, null while the link is off. Owned by the
   *  page's photoset copy (usePropertyAssets), seeded from the backend. */
  token: string | null;
  /** Adopt a minted token, or null after a revoke. */
  onTokenChange: (token: string | null) => void;
}

export interface PublicLink {
  /** The live link's share token, or null while it is off. */
  token: string | null;
  /** The live link, or null while it is off. */
  url: string | null;
  /** A mint or revoke is in flight. */
  busy: boolean;
  /** Switch the link on (mint) or off (revoke). */
  setOn: (on: boolean) => void;
  copy: () => void;
  /** True for a moment after a successful copy. */
  copied: boolean;
}

/**
 * The PlanOtour link's state and actions, shared by the header's quick
 * menu and the Share modal's PlanOtour tab so both are the same switch
 * with the same toasts (DV-3714).
 *
 * Turning the link off retires the URL for good: asset-service deletes the
 * token, so turning it on again mints a NEW one and a leaked link stays dead.
 */
export const usePublicLink = ({ photosetId, token, onTokenChange }: UsePublicLinkArgs): PublicLink => {
  const dispatch = useDispatch();
  const [createLink, { isLoading: creating }] = useCreatePublicLinkMutation();
  const [revokeLink, { isLoading: revoking }] = useRevokePublicLinkMutation();
  const [copied, setCopied] = useState(false);

  // "Copied" describes one URL; a new link or another photoset's starts fresh.
  useEffect(() => {
    setCopied(false);
  }, [photosetId, token]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const alertFailure = (error: unknown) =>
    dispatch(
      showAlert({
        type: Variants.ERROR,
        message: publicLinkErrorMessage(error),
        apiResponse: error
      })
    );

  const turnOn = async () => {
    try {
      const result = await createLink(photosetId).unwrap();
      onTokenChange(result.token);
      dispatch(showAlert({ type: Variants.SUCCESS, message: PUBLIC_LINK_TOASTS.on }));
    } catch (error: unknown) {
      alertFailure(error);
    }
  };

  const turnOff = async () => {
    try {
      await revokeLink(photosetId).unwrap();
      onTokenChange(null);
      dispatch(showAlert({ type: Variants.INFO, message: PUBLIC_LINK_TOASTS.off }));
    } catch (error: unknown) {
      alertFailure(error);
    }
  };

  const url = token === null ? null : publicGalleryUrl(token);

  const copy = async () => {
    if (url === null) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      dispatch(showAlert({ type: Variants.WARNING, message: PUBLIC_LINK_TOASTS.copyFailed }));
    }
  };

  const setOn = (on: boolean) => {
    if (on) void turnOn();
    else void turnOff();
  };

  return {
    token,
    url,
    busy: creating || revoking,
    setOn,
    copy: () => void copy(),
    copied
  };
};
